/**
 * Trainer Service
 *
 * Service for managing trainer-client relationships including:
 * - Requesting a trainer
 * - Listing clients (for trainers)
 * - Accepting / declining client requests
 * - Ending trainer-client relationships
 *
 * Extends the base CrudService for shared behavior
 * and uses direct apiClient calls where needed.
 */

import { ZodError } from 'zod';

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { CrudService } from './crud.service';
import { invalidDataErrorFromZod } from '../errors/invalid-data.error';
import {
  type DeclareTrainingHoldSchemaInput,
  type DeclareTrainingHoldSchemaOutput,
  type ProposeGoalChangeSchemaInput,
  type ProposePlanChangeSchemaInput,
  type ProposeScheduleChangeSchemaInput,
  type SendBumpSchemaInput,
  type SendBumpSchemaOutput,
  validateProposeGoalChange,
  validateProposePlanChange,
  validateProposeScheduleChange,
  validateDeclareTrainingHold,
  validateSendBump,
} from '../validation/trainer.schemas';

import type { PlanTemplateResponse } from './template.service';
import type { SuccessApiResponse } from '../types/api.types';
import type { ID } from '../types/common/id.types';
import type { PaginatedResponse } from '../types/common/pagination.types';
import type {
  ChangeProposalListRow,
  CoachingAttentionRow,
  TrainerBumpReason,
  TrainerRequestPayload,
  TrainerContractTerms,
  TrainerRequestAction,
  TrainerRequestActionPayload,
  TrainerClient,
  TrainerClientDetail,
  TrainerClientQuery,
  TrainerClientRead,
  TrainerListing,
  TrainerRequestResponse,
} from '../types/domain/trainer.types';

// =============================================================================
// TRAINER SERVICE CLASS
// =============================================================================

/**
 * `CrudService<TDetail, TList, TRequest>` — the detail and list shapes are
 * deliberately different. The roster row carries the coaching score and the
 * relationship; the client view page carries the profile alongside it.
 */
export class TrainerService extends CrudService<
  TrainerClientDetail,
  TrainerClient,
  TrainerRequestPayload
> {
  constructor(cacheTTL?: number) {
    super({
      baseUrl: '/trainer',
      enableCaching: true,
      cacheTTL: cacheTTL || 5 * 60 * 1000, // 5 minutes default
    });
  }

  // ---------------------------------------------------------------------------
  // READ OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * Get the authenticated trainer's roster.
   *
   * GET /api/trainer/clients
   *
   * Returns accepted clients and pending requests in one list — partition on
   * `status`. Server-side `search`, `sort` and `direction` are supported; an
   * unrecognised sort key silently falls back to name rather than erroring.
   *
   * The endpoint paginates at 10 by default, so this passes `paginate: 'false'`
   * unless the caller asks otherwise. Use {@link getClientsPaginated} when the
   * page numbers are actually needed.
   */
  public async getClients(
    query: TrainerClientQuery = {}
  ): Promise<TrainerClient[]> {
    try {
      // The collection is wrapped in `{ data: [...] }`, and gains `links`/`meta`
      // when paginated. Tolerate a bare array too — some MSW fixtures still
      // return the unwrapped shape.
      const response = await apiClient.get<
        TrainerClient[] | { data: TrainerClient[] }
      >('/trainer/clients', {
        params: { paginate: 'false', ...query },
      });

      const body = response.data;
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      rethrowTyped(error, 'Unable to fetch clients. Please try again.');
    }
  }

  /**
   * Get one page of the trainer's roster, with the pagination envelope intact.
   *
   * GET /api/trainer/clients
   */
  public async getClientsPaginated(
    query: TrainerClientQuery = {}
  ): Promise<PaginatedResponse<TrainerClient>> {
    try {
      const response = await apiClient.get<PaginatedResponse<TrainerClient>>(
        '/trainer/clients',
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      rethrowTyped(error, 'Unable to fetch clients. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // WRITE OPERATIONS
  // ---------------------------------------------------------------------------

  /**
   * The client-facing trainer directory.
   *
   * GET /api/trainer
   *
   * Client-role only — the endpoint returns `[]` for anybody else rather than
   * refusing, so an empty array is not proof the directory is empty.
   *
   * Deliberately **not** the inherited `getAll()`. `CrudService` is typed for
   * this service's roster shapes, so `getAll()` would hand back a
   * `TrainerClient[]` for a payload that is nothing of the sort; the directory
   * has its own shape and needs its own method.
   *
   * Takes no parameters because the endpoint accepts none: no search, no sort,
   * no pagination. The whole list arrives at once.
   */
  public async getTrainers(): Promise<TrainerListing[]> {
    try {
      // `response()->success()` wraps in `{ data: … }`. A bare array is
      // tolerated for the same reason `getClients` tolerates one — some MSW
      // fixtures still return the unwrapped shape.
      const response = await apiClient.get<
        TrainerListing[] | { data: TrainerListing[] }
      >('/trainer');

      const body = response.data;
      return Array.isArray(body) ? body : (body?.data ?? []);
    } catch (error) {
      console.error('Failed to fetch trainers:', error);
      rethrowTyped(error, 'Unable to load trainers. Please try again.');
    }
  }

  /**
   * Request a trainer by username.
   *
   * POST /api/trainer
   *
   * The API returns 201 status on success with a message.
   *
   * `note` reaches `trainer_client.note` and is rendered to the trainer in
   * their pending-request panel. It is validated `nullable|string` with **no
   * maximum**, so nothing server-side stops an unbounded paste — the composing
   * UI is the only place that can keep it civil.
   *
   * A duplicate request is a **silent no-op that still answers 201**: the
   * action returns early when a non-expired contract already exists for the
   * pair. A caller cannot distinguish "sent" from "already asked" by the
   * response, so do not report one as the other with any confidence.
   */
  public async requestTrainer(
    username: ID,
    note?: string | null
  ): Promise<TrainerRequestResponse> {
    if (!username) {
      throw new Error(
        'Trainer username is required to send a trainer request.'
      );
    }

    try {
      // Sent only when there is something to send: `validate()` drops absent
      // optional keys, and posting `note: ''` would store an empty string
      // where the trainer's panel expects null and renders no quote block.
      const trimmed = typeof note === 'string' ? note.trim() : '';
      const payload: TrainerRequestPayload = {
        username,
        ...(trimmed ? { note: trimmed } : {}),
      };
      const response = await apiClient.post<TrainerRequestResponse>(
        '/trainer',
        payload
      );

      if (response.status !== 201) {
        throw new Error('Failed to send trainer request');
      }

      return response.data;
    } catch (error) {
      console.error('Failed to send trainer request:', error);
      rethrowTyped(error, 'Unable to send trainer request. Please try again.');
    }
  }

  /**
   * Accept or decline a pending client request.
   *
   * PATCH /api/trainer/{username}
   *
   * Accepting requires the contract terms — the API answers a bare
   * `{ action: 'accept' }` with a 422 — so they are refused here before the
   * round trip. Declining sends the action alone.
   *
   * The API returns 200 status on success.
   */
  public async respondToClientRequest(
    username: ID,
    action: TrainerRequestAction,
    terms?: TrainerContractTerms
  ): Promise<void> {
    if (!username) {
      throw new Error('Client username is required to respond to request.');
    }

    if (!action) {
      throw new Error('Action is required to respond to client request.');
    }

    if (action === 'accept' && !terms) {
      throw new Error(
        'Contract terms are required to accept a client request.'
      );
    }

    try {
      const payload: TrainerRequestActionPayload =
        action === 'accept' && terms
          ? { action: 'accept', ...terms }
          : { action: 'decline' };
      const response = await apiClient.patch(`/trainer/${username}`, payload);

      if (response.status !== 200) {
        throw new Error(`Failed to respond to client request for ${username}`);
      }

      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error(`Failed to respond to client request ${username}:`, error);
      rethrowTyped(
        error,
        'Unable to respond to client request. Please try again.'
      );
    }
  }

  /**
   * Rate a trainer out of ten, as their client.
   *
   * PATCH /api/trainer/{username}/rate
   *
   * Client-role only, and only where a contract exists: live, or ended inside
   * the last `TrainerClient::GRACE_DAYS` (14). Past that the API refuses with
   * "Cannot rate: contract has expired." Pending and blocked pairs are refused
   * outright, and an expired contract carrying no `ends_at` is a silent no-op.
   *
   * The score is stored per contract and re-averaged across every rating the
   * trainer holds, so one call moves their published figure rather than
   * setting it.
   */
  public async rateTrainer(username: ID, rating: number): Promise<void> {
    if (!username) {
      throw new Error('Trainer username is required to rate a trainer.');
    }

    // `integer:strict` server-side: a float or a numeric string is a 422, and
    // the range is inclusive at both ends.
    if (!Number.isInteger(rating) || rating < 1 || rating > 10) {
      throw new Error('Rating must be a whole number between 1 and 10.');
    }

    try {
      await apiClient.patch(`/trainer/${username}/rate`, { rating });
      // The directory's `rating` and `ratings` are now stale for every reader.
      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error(`Failed to rate trainer ${username}:`, error);
      rethrowTyped(error, 'Unable to save your rating. Please try again.');
    }
  }

  /**
   * End a trainer-client relationship.
   *
   * DELETE /api/trainer/{username}
   *
   * The API answers 204 with no body; any 2xx counts as ended.
   */
  public async endRelationship(username: string): Promise<void> {
    if (!username) {
      throw new Error('Client username is required to end relationship.');
    }

    try {
      const response = await apiClient.delete(`/trainer/${username}`);

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to end relationship with ${username}`);
      }

      this.invalidateCache(['getAll']);
    } catch (error) {
      console.error(`Failed to end relationship with ${username}:`, error);
      rethrowTyped(error, 'Unable to end relationship. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // COACHING ATTENTION
  // ---------------------------------------------------------------------------

  /**
   * Get the coaching attention feed.
   *
   * GET /api/trainer/client-risk-alerts
   *
   * One row per client — every accepted client, not only the flagged ones,
   * because the card folds steady clients into a count and renders held clients
   * as present-but-unjudged. Despite the route name, the representation groups
   * by client: a client with three open alerts is one row.
   *
   * The endpoint ranks nothing. State, urgency, caps and folds are the card's.
   */
  public async getCoachingAttention(): Promise<CoachingAttentionRow[]> {
    try {
      const response = await apiClient.get<{ data: CoachingAttentionRow[] }>(
        '/trainer/client-risk-alerts'
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch the coaching attention feed:', error);
      rethrowTyped(
        error,
        'Unable to load who needs you this week. Please try again.'
      );
    }
  }

  // ---------------------------------------------------------------------------
  // WRITE OPERATIONS — bumps and holds
  // ---------------------------------------------------------------------------

  /**
   * Send a bump.
   *
   * POST /api/trainer/{username}/client-bump
   *
   * Exactly one of `reason` / `alertId`. Passing both is a programming error
   * rather than a user one, so it fails here instead of costing a round trip to
   * learn the same thing from a 422.
   *
   * Callers must gate this on the alert's `bumpable` flag — the server refuses
   * an ACWR alert, a rest-held client and an alert already answered by an
   * unopened bump, and the last of those is a 409 rather than a 422.
   */
  public async sendBump(
    username: ID,
    options: { reason?: TrainerBumpReason; alertId?: string } = {}
  ): Promise<void> {
    if (!username) {
      throw new Error('Client username is required to send a bump.');
    }

    const { reason, alertId } = options;

    // Composed through branches rather than a spread, because
    // `exactOptionalPropertyTypes` distinguishes an absent key from one set to
    // `undefined` — and the schema's exactly-one-of rule reads the two
    // differently. The schema is what rejects both-or-neither.
    const payload: SendBumpSchemaInput = {
      ...(alertId !== undefined ? { alert_id: alertId } : {}),
      ...(reason !== undefined ? { reason } : {}),
    };

    // Send what the schema produced, not what came in. The other services in
    // this package discard the parse result, which silently drops every
    // transform: a `.trim()`ed field validates at its trimmed length and then
    // goes over the wire untrimmed, so a value the schema accepted can still
    // fail the server's own length rule.
    const body = this.validateBumpData(payload);

    try {
      const response = await apiClient.post(
        `/trainer/${username}/client-bump`,
        body
      );

      if (response.status !== 201) {
        throw new Error(`Failed to bump ${username}`);
      }
    } catch (error) {
      console.error(`Failed to bump ${username}:`, error);
      rethrowTyped(error, 'Unable to send the bump. Please try again.');
    }
  }

  /**
   * Declare a training hold.
   *
   * POST /api/trainer/{username}/training-hold
   *
   * There is at most one open hold per pair, so this is a create, not an
   * upsert. Omitting `expected_end` makes it open-ended: it never auto-returns
   * and the trainer closes it themselves.
   */
  public async declareHold(
    username: ID,
    payload: DeclareTrainingHoldSchemaInput
  ): Promise<void> {
    if (!username) {
      throw new Error('Client username is required to declare a hold.');
    }

    // Parsed, not raw — see the note in sendBump. A 255-character note with
    // trailing whitespace validates here and 422s at the server otherwise.
    const body = this.validateHoldData(payload);

    try {
      const response = await apiClient.post(
        `/trainer/${username}/training-hold`,
        body
      );

      if (response.status !== 201) {
        throw new Error(`Failed to declare a hold for ${username}`);
      }
    } catch (error) {
      console.error(`Failed to declare a hold for ${username}:`, error);
      rethrowTyped(error, 'Unable to save the hold. Please try again.');
    }
  }

  /**
   * End the open training hold.
   *
   * DELETE /api/trainer/{username}/training-hold
   *
   * One path for both directions — resuming early and confirming a return are
   * the same operation, because there is at most one open hold per pair.
   */
  public async endHold(username: ID): Promise<void> {
    if (!username) {
      throw new Error('Client username is required to end a hold.');
    }

    try {
      const response = await apiClient.delete(
        `/trainer/${username}/training-hold`
      );

      // Any 2xx: the local API answers a DELETE with 204, and a 200 that
      // was once expected here turned a hold that had ended into a red
      // toast (found on device, 2026-10-09).
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to end the hold for ${username}`);
      }
    } catch (error) {
      console.error(`Failed to end the hold for ${username}:`, error);
      rethrowTyped(error, 'Unable to end the hold. Please try again.');
    }
  }

  // ---------------------------------------------------------------------------
  // VALIDATION
  // ---------------------------------------------------------------------------

  /**
   * Validate a bump payload using the Zod schema.
   *
   * Outside the try/catch on purpose: an `InvalidDataError` carries per-field
   * `validationErrors`, and folding it through `rethrowTyped` would flatten
   * that into a generic network-ish message.
   */
  // ===========================================================================
  // CHANGE PROPOSALS
  // ===========================================================================

  /**
   * Propose a change to the sets in a client's plan.
   *
   * **The plan must be one this trainer prescribed.** `PlanPolicy` allows the
   * owner or the plan's author, so a `self_directed` plan is not theirs to
   * propose against and the server refuses it.
   *
   * No `kind` goes over the wire — there is an endpoint per kind and the server
   * derives it from the route.
   *
   * @throws 409 when a plan proposal is already pending for this client
   */
  public async proposePlanChange(
    username: ID,
    payload: ProposePlanChangeSchemaInput
  ): Promise<void> {
    await this.propose(
      username,
      'plan-proposal',
      this.validateProposal(
        validateProposePlanChange,
        payload,
        'proposePlanChange'
      ),
      'proposePlanChange'
    );
  }

  /**
   * Propose a change to which days a client's plan asks for.
   *
   * Separate from {@link proposePlanChange} because it writes somewhere else
   * entirely — and because the two are mutually exclusive: one pending plan
   * proposal per client, whichever kind.
   *
   * @throws 409 when a plan proposal is already pending for this client
   */
  public async proposeScheduleChange(
    username: ID,
    payload: ProposeScheduleChangeSchemaInput
  ): Promise<void> {
    await this.propose(
      username,
      'schedule-proposal',
      this.validateProposal(
        validateProposeScheduleChange,
        payload,
        'proposeScheduleChange'
      ),
      'proposeScheduleChange'
    );
  }

  /**
   * Propose a new target or deadline on a client's goal.
   *
   * **Paywalled.** Gated behind `client_goal_setting`, so this is the one
   * proposal that can 402 — and it is a mutation, so the caller surfaces the
   * upgrade prompt rather than throwing into a read boundary.
   *
   * @throws 409 when a goal proposal is already pending for this client — for
   *         *any* of their goals, not just this one
   */
  public async proposeGoalChange(
    username: ID,
    payload: ProposeGoalChangeSchemaInput
  ): Promise<void> {
    await this.propose(
      username,
      'goal-proposal',
      this.validateProposal(
        validateProposeGoalChange,
        payload,
        'proposeGoalChange'
      ),
      'proposeGoalChange'
    );
  }

  /**
   * A client's plan, in the template shape the plan editor reads — the first
   * step of proposing a change to it.
   *
   * GET /api/trainer/plan/{plan}/template
   *
   * The owner's `GET /template/plan/{id}` stays owner-only, so a trainer reads
   * a client's plan here. `planId` is the `plan_id` the coaching feed gives for
   * a client whose plan is `prescribed`. Access is the rule
   * {@link proposePlanChange} applies — this trainer prescribed it and their
   * contract is in force — so "can load it to propose" and "can propose" never
   * disagree. Anyone else, clients included, gets a 404, never a 403.
   *
   * Weight and distance arrive in the trainer's own measurement system and go
   * back unchanged: the proposal endpoint reads the trainer's units. The uuids
   * are minted fresh on every read — form keys, never identities.
   *
   * Still answers while a proposal is pending; it is the send that refuses,
   * with a 409.
   */
  public async getProposalPlanTemplate(
    planId: ID
  ): Promise<PlanTemplateResponse> {
    if (!planId) {
      throw new Error('A plan id is required to load it for a proposal.');
    }

    try {
      const response = await apiClient.get<
        SuccessApiResponse<PlanTemplateResponse>
      >(`/trainer/plan/${planId}/template`);

      return response.data.data;
    } catch (error) {
      console.error(`Failed to load plan ${planId} for a proposal:`, error);
      rethrowTyped(error, 'Unable to load their plan. Please try again.');
    }
  }

  /**
   * One client on this trainer's roster.
   *
   * GET /api/trainer/clients/{username}
   *
   * Read for its `proposals` slots before a proposal editor opens: the API
   * holds one pending proposal per subject per client. Unlike the attention
   * feed it needs only the trainer role, so the check runs for every trainer.
   * A 404 means the username is not on this trainer's roster.
   */
  public async getClient(username: string): Promise<TrainerClientRead> {
    if (!username) {
      throw new Error('A username is required to read a client.');
    }

    try {
      const response = await apiClient.get<
        SuccessApiResponse<TrainerClientRead>
      >(`/trainer/clients/${encodeURIComponent(username)}`);

      return response.data.data;
    } catch (error) {
      console.error(`Failed to read client ${username}:`, error);
      rethrowTyped(error, 'Unable to load this client. Please try again.');
    }
  }

  /**
   * The trainer's outbox — every proposal they have sent.
   *
   * Rows carry a flat `diff_summary` and no breakdown; the full diff lives on
   * `changeProposalService.get()`.
   */
  public async getProposals(): Promise<ChangeProposalListRow[]> {
    try {
      const response = await apiClient.get<{ data: ChangeProposalListRow[] }>(
        '/trainer/change-proposal'
      );

      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch change proposals:', error);
      rethrowTyped(
        error,
        'Unable to load the changes you have proposed. Please try again.'
      );
    }
  }

  /**
   * Withdraw a proposal before it is answered.
   *
   * A status transition rather than a delete, and — with no supersede rule —
   * the only route to replacing a proposal already sent.
   */
  public async rescindProposal(proposalId: string): Promise<void> {
    if (!proposalId) {
      throw new Error('A proposal id is required to withdraw it.');
    }

    try {
      const response = await apiClient.delete(
        `/trainer/change-proposal/${proposalId}`
      );

      // Any 2xx: the API answers 204 with no body.
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to withdraw proposal ${proposalId}`);
      }
    } catch (error) {
      console.error(`Failed to withdraw proposal ${proposalId}:`, error);
      rethrowTyped(error, 'Unable to withdraw the change. Please try again.');
    }
  }

  /**
   * Nudge a client about a proposal they have not answered.
   *
   * Offer this only where the feed's `remindable` is true. **That flag is not
   * `bumpable`** — reusing the bump gate would strip the reminder from
   * injury-risk rows, which are the ones most likely to be carrying a deload
   * proposal.
   *
   * @throws 409 when the proposal is unanswerable or already nudged
   */
  public async remindAboutProposal(proposalId: string): Promise<void> {
    if (!proposalId) {
      throw new Error('A proposal id is required to send a reminder.');
    }

    try {
      const response = await apiClient.post(
        `/trainer/change-proposal/${proposalId}/reminder`,
        {}
      );

      if (response.status !== 201) {
        throw new Error(`Failed to remind about proposal ${proposalId}`);
      }
    } catch (error) {
      console.error(`Failed to remind about proposal ${proposalId}:`, error);
      rethrowTyped(error, 'Unable to send the reminder. Please try again.');
    }
  }

  /**
   * Validate a proposal payload using its Zod schema.
   *
   * The same treatment {@link validateBumpData} and the hold validator get, and
   * for the same reason: a raw `ZodError` reaching a caller carries its issues
   * in a shape nothing downstream reads, so a form cannot put each message back
   * on the field that caused it. All three proposal forms validate before they
   * submit, which is why this had gone unnoticed — but the schema is the last
   * line, not the first, and it should refuse in the same currency as its
   * siblings.
   *
   * Generic rather than three near-identical private methods: only the
   * operation name differs, and the message would be the same sentence three
   * times.
   */
  private validateProposal<T>(
    validate: (data: unknown) => T,
    payload: unknown,
    operation: string
  ): T {
    try {
      return validate(payload);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't send the change", error, {
          originalData: payload,
          details: { service: 'TrainerService', operation },
        });
      }

      throw error;
    }
  }

  /**
   * The shared half of the three propose calls.
   *
   * Sends what the schema produced rather than what came in: the callers parse
   * before this, so a `.trim()`ed note goes over the wire trimmed and cannot
   * pass here only to fail the server's own length rule.
   */
  private async propose(
    username: ID,
    path: 'plan-proposal' | 'schedule-proposal' | 'goal-proposal',
    body: unknown,
    operation: string
  ): Promise<void> {
    if (!username) {
      throw new Error('Client username is required to propose a change.');
    }

    try {
      const response = await apiClient.post(
        `/trainer/${username}/${path}`,
        body
      );

      if (response.status !== 201) {
        throw new Error(`Failed to ${operation} for ${username}`);
      }
    } catch (error) {
      console.error(`Failed to ${operation} for ${username}:`, error);
      rethrowTyped(error, 'Unable to send the change. Please try again.');
    }
  }

  private validateBumpData(payload: SendBumpSchemaInput): SendBumpSchemaOutput {
    try {
      return validateSendBump(payload);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't send the bump", error, {
          originalData: payload,
          details: { service: 'TrainerService', operation: 'sendBump' },
        });
      }

      throw error;
    }
  }

  /**
   * Validate a declare-hold payload using the Zod schema.
   *
   * The same schema backs the declare-hold form's resolver, so the rules a
   * coach sees inline are the rules this enforces.
   */
  private validateHoldData(
    payload: DeclareTrainingHoldSchemaInput
  ): DeclareTrainingHoldSchemaOutput {
    try {
      return validateDeclareTrainingHold(payload);
    } catch (error) {
      if (error instanceof ZodError) {
        throw invalidDataErrorFromZod("Couldn't save the hold", error, {
          originalData: payload,
          details: { service: 'TrainerService', operation: 'declareHold' },
        });
      }

      throw error;
    }
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default trainer service instance.
 */
export const trainerService = new TrainerService();

export default TrainerService;
